import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import MobilePageBar from '../components/MobilePageBar';
import { FiPlus } from 'react-icons/fi';
import CreateProjectModal from '../components/CreateProjectModal';
import EditProjectModal from '../components/EditProjectModal';
import { ProjectAccess } from '../hooks/useProjectAccess';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Project {
  id: string;
  name: string;
  description: string | null;
  status: string;
  platform: string;
  audience: string;
  video_count: number;
  shorts_count: number;
  caption_status: string | null;
  default_linked_account_id?: string | null;
  access?: ProjectAccess;
  created_at: string;
  updated_at: string;
}

const CAPTION_STATUS_LABEL: Record<string, string> = {
  draft: 'Caption: Draft',
  processing: 'Caption: Processing',
  ready: 'Caption: Ready',
  failed: 'Caption: Failed',
};

const CAPTION_STATUS_BADGE_CLASS: Record<string, string> = {
  draft: 'pending',
  processing: 'processing',
  ready: 'done',
  failed: 'failed',
};

const PlusIcon = FiPlus as unknown as React.FC<{ 'aria-hidden'?: boolean }>;

const ProjectList: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showModal, setShowModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [shareProject, setShareProject] = useState<Project | null>(null);
  const navigate = useNavigate();

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/projects`, { credentials: 'include' });
      if (res.ok) setProjects(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchProjects(); }, []);

  // Poll while any project has a non-terminal caption workspace
  useEffect(() => {
    const hasProcessing = projects.some(p => p.caption_status && !['ready', 'failed'].includes(p.caption_status));
    if (!hasProcessing) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/projects`, { credentials: 'include' });
        if (res.ok) setProjects(await res.json());
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [projects]);

  const filtered = useMemo(() =>
    projects.filter(p => p.name.toLowerCase().includes(search.toLowerCase())),
    [projects, search]
  );

  const handleDelete = async (id: string) => {
    if (!window.confirm('Archive this project?')) return;
    await fetch(`${API_BASE}/api/projects/${id}`, { method: 'DELETE', credentials: 'include' });
    setProjects(prev => prev.filter(p => p.id !== id));
    setMenuOpen(null);
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />
        <MobilePageBar current="Projects" label="📁 Projects" showCrumb={false}>
          <button
            type="button"
            className="mobile-page-btn"
            onClick={() => setShowModal(true)}
            title="New project"
            aria-label="New project"
          >
            <PlusIcon aria-hidden={true} />
          </button>
        </MobilePageBar>
        <div className="page-header posts-list-header">
          <div className="page-header-left">
            <h2>My Projects</h2>
          </div>
          <button className="header-action-btn header-action-primary" onClick={() => setShowModal(true)}>+ New Project</button>
        </div>

        <div className="project-toolbar">
          <div className="project-search">
            <span className="search-icon-sm">🔍</span>
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="project-view-toggle">
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>⊞</button>
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>⊟</button>
          </div>
        </div>

        {loading ? (
          <div className="project-loading">Loading projects...</div>
        ) : filtered.length === 0 ? (
          <div className="project-empty">
            <div className="empty-icon">📁</div>
            <h3>{search ? 'No matching projects' : 'No projects yet'}</h3>
            <p>Create your first project to start converting videos.</p>
            {!search && (
              <button className="btn-primary" onClick={() => setShowModal(true)}>+ Create Project</button>
            )}
          </div>
        ) : (
          <div className={`project-grid ${viewMode}`}>
            {filtered.map(p => (
              <div key={p.id} className="project-card" onClick={() => navigate(`/projects/${p.id}`)}>
                <div className="project-card-thumb">
                  <span>📁</span>
                </div>
                <div className="project-card-body">
                  <h3 className="project-card-name">
                    {p.name}
                    {p.access && !p.access.isOwner && <span className="project-shared-badge" title={`Shared with you as ${p.access.role}`}>👁 Shared</span>}
                  </h3>
                  <p className="project-card-meta">
                    {p.video_count} video{p.video_count !== 1 ? 's' : ''} · {p.shorts_count} short{p.shorts_count !== 1 ? 's' : ''}
                    {p.caption_status && (
                      <span className={`video-status-badge video-status-${CAPTION_STATUS_BADGE_CLASS[p.caption_status] || 'pending'}`}>
                        {CAPTION_STATUS_LABEL[p.caption_status] || p.caption_status}
                      </span>
                    )}
                  </p>
                  <p className="project-card-time">Updated {timeAgo(p.updated_at)}</p>
                </div>
                <div className="project-card-menu" onClick={e => { e.stopPropagation(); setMenuOpen(menuOpen === p.id ? null : p.id); }}>
                  ⋯
                  {menuOpen === p.id && (
                    <div className="project-card-dropdown">
                      <button onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}>Open</button>
                      {(!p.access || p.access.isOwner) && (
                        <>
                          <button onClick={e => { e.stopPropagation(); setMenuOpen(null); setShareProject(p); }}>Share as Demo</button>
                          <button onClick={e => { e.stopPropagation(); handleDelete(p.id); }} className="danger">Archive</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {shareProject && (
        <EditProjectModal
          project={shareProject}
          initialTab="share"
          onClose={() => setShareProject(null)}
          onSaved={updated => {
            setProjects(prev => prev.map(x => (x.id === shareProject.id ? { ...x, ...updated } : x)));
            setShareProject(null);
          }}
        />
      )}

      {showModal && (
        <CreateProjectModal
          onClose={() => setShowModal(false)}
          onCreate={project => {
            setProjects(prev => [project, ...prev]);
            setShowModal(false);
            navigate(`/projects/${project.id}`);
          }}
        />
      )}
    </div>
  );
};

export default ProjectList;
